// Ricostruisce le righe di review (PlanLineDraft) da un import GIÀ committato,
// leggendo gli eventi stock_in del documento. PURO. Ri-valida ogni riga col
// modulo P1 (validateImportLine) così i dati sbagliati del carico originale
// (lotto-frase, unità incoerente) escono a semaforo rosso. NIENTE AI.
import { validateImportLine } from '@trackeat/shared';
import type { PlanLineDraft } from './CapturePhotoPage.js';
import { convertQty, normalizeUom } from '../../lib/uom.js';
import { roundMoney } from '../../lib/money.js';

export interface LoadedLine {
  seq: number;
  storage_unit_id?: string | null;
  supplier_sku?: string | null;
  is_frozen?: boolean;
  notify_expiration?: boolean;
  keep_current_lot?: boolean;
  iva_rate?: number | null;
  food_id: string | null;
  description: string;
  // Campi documento immutabili. Assenti sui payload legacy, dove qty/uom e
  // unit_price rappresentavano contemporaneamente documento e magazzino.
  source_qty?: number | null;
  source_uom?: string | null;
  source_unit_price?: number | null;
  source_line_total?: number | null;
  currency?: string | null;
  // Coordinate derivate del movimento di magazzino.
  stock_qty?: number | null;
  stock_uom?: string | null;
  stock_unit_cost?: number | null;
  conversion_factor?: number | null;
  conversion_source?: string | null;
  // Alias legacy, mantenuti per ricostruire import precedenti a T-481.
  qty: number;
  uom: string;
  batch_lot: string | null;
  expiration_date: string | null;
  unit_price: number | null;
  line_total?: number | null;
}

export interface FoodLite {
  id: string;
  name: string;
  default_uom: string;
  category: string | null;
  storage_type: string | null;
}

export interface CorrectionDocumentTotalDraft {
  parsed: number | null;
  input: string;
}

/**
 * Conserva il totale fiscale durante "Correggi importazione". Un valore
 * assente resta davvero assente: non sintetizziamo 0 per superare il gate
 * economico del server.
 */
export function correctionDocumentTotalDraft(
  value: number | null | undefined,
): CorrectionDocumentTotalDraft {
  if (value == null || !Number.isFinite(value) || value < 0) {
    return { parsed: null, input: '' };
  }
  return { parsed: value, input: String(value) };
}

// PlanLineDraft ha molti campi; qui li riempiamo con i default coerenti col
// commit. I campi non pertinenti alla ricostruzione (pack_*, ai_description…)
// vanno ai loro default neutri.
function emptyFoodCreate(): PlanLineDraft['food_create'] {
  return { name: '', default_uom: 'pz', storage_type: '', category: '', allergens: [] };
}

/**
 * Deriva il costo sull'unità di magazzino senza mai riscrivere il prezzo
 * unitario del documento. Il totale riga, quando presente, è autorevole.
 */
export function deriveStockUnitCost(input: {
  sourceQty: number | null;
  sourceUnitPrice: number | null;
  sourceLineTotal: number | null;
  stockQty: number | null;
}): number | null {
  const { sourceQty, sourceUnitPrice, sourceLineTotal, stockQty } = input;
  if (stockQty == null || !Number.isFinite(stockQty) || stockQty <= 0) return null;
  if (sourceLineTotal != null && Number.isFinite(sourceLineTotal)) {
    return sourceLineTotal / stockQty;
  }
  if (
    sourceQty == null || !Number.isFinite(sourceQty) || sourceQty <= 0
    || sourceUnitPrice == null || !Number.isFinite(sourceUnitPrice)
  ) {
    return null;
  }
  return (sourceUnitPrice * sourceQty) / stockQty;
}

export interface SourceEconomicDraft {
  sourceUnitPrice: number | null;
  sourceLineTotal: number | null;
}

function finiteNonNegative(value: number | null): number | null {
  return value != null && Number.isFinite(value) && value >= 0 ? value : null;
}

/**
 * Il prezzo e il totale visibili in review sono una coppia coerente.
 * Correggere il prezzo rende quel prezzo autorevole e ricalcola il totale sulla
 * quantità documentale; cancellarlo invalida anche il totale, evitando che un
 * vecchio totale OCR continui silenziosamente a governare il carrying value.
 */
export function sourceEconomicsFromUnitPrice(
  sourceQty: number | null,
  editedUnitPrice: number | null,
): SourceEconomicDraft {
  const qty = finitePositive(sourceQty);
  const rawUnitPrice = finiteNonNegative(editedUnitPrice);
  const unitPrice = rawUnitPrice == null ? null : roundMoney(rawUnitPrice);
  return {
    sourceUnitPrice: unitPrice,
    sourceLineTotal: qty != null && unitPrice != null
      ? roundMoney(qty * unitPrice)
      : null,
  };
}

/**
 * Il totale riga è autorevole quando viene corretto dall'operatore. Con una
 * quantità documentale valida aggiorna anche il prezzo medio mostrato; se il
 * totale viene cancellato, entrambi i valori diventano ignoti.
 */
export function sourceEconomicsFromLineTotal(
  sourceQty: number | null,
  editedLineTotal: number | null,
): SourceEconomicDraft {
  const qty = finitePositive(sourceQty);
  const rawLineTotal = finiteNonNegative(editedLineTotal);
  const lineTotal = rawLineTotal == null ? null : roundMoney(rawLineTotal);
  return {
    sourceUnitPrice: qty != null && lineTotal != null
      ? roundMoney(lineTotal / qty)
      : null,
    sourceLineTotal: lineTotal,
  };
}

export interface StockUomDraftChange {
  qty: string;
  uom: string;
  conversionFactor: number | null;
  conversionSource: string;
  conversionConfirmed: boolean;
}

export interface StockQuantityDraftEdit {
  qty: string;
  sourceQty: number;
  sourceUnitPrice: number | null;
  sourceLineTotal: number | null;
  conversionFactor: number | null;
  conversionSource: string;
  conversionConfirmed: boolean;
}

export interface SourceQuantityDraftEdit {
  sourceQty: number;
  stockQty: string;
  sourceUnitPrice: number | null;
  sourceLineTotal: number | null;
  conversionFactor: number | null;
  conversionSource: string;
  conversionConfirmed: boolean;
}

export interface PackFactorDraftApply {
  sourceQty: number;
  stockQty: string;
  factor: number;
}

export interface SourceUomDraftChange {
  stockQty: string;
  conversionFactor: number | null;
  conversionSource: string;
  conversionConfirmed: boolean;
}

function finitePositive(value: number | string | null | undefined): number | null {
  const parsed = typeof value === 'string'
    ? Number(value.replace(',', '.'))
    : value;
  return typeof parsed === 'number' && Number.isFinite(parsed) && parsed > 0
    ? parsed
    : null;
}

function compactQuantity(value: number): string {
  return String(Number(value.toPrecision(15)));
}

/**
 * Corregge il numero di pezzi/colli letto sul documento senza confonderlo con
 * la quantità fisica già convertita.
 *
 * - identity/metric/fattore per collo: ricalcola lo stock dalla nuova sorgente;
 * - document_weight: il peso totale scritto sul documento resta autorevole e
 *   viene ricalcolato soltanto il rapporto per pezzo;
 * - il totale riga, se presente, resta autorevole e aggiorna il prezzo medio.
 */
export function editSourceQuantityDraft(input: {
  nextSourceQty: number | string | null | undefined;
  currentStockQty: number | string | null | undefined;
  stockUom: string | null | undefined;
  sourceQty: number;
  sourceUom: string | null | undefined;
  sourceUnitPrice: number | null;
  sourceLineTotal: number | null;
  conversionFactor: number | null;
  conversionSource: string | null;
}): SourceQuantityDraftEdit | null {
  const nextSourceQty = finitePositive(input.nextSourceQty);
  const currentStockQty = finitePositive(input.currentStockQty);
  if (nextSourceQty == null || currentStockQty == null) return null;

  const explicitTotal = finiteNonNegative(input.sourceLineTotal);
  const unitPrice = finiteNonNegative(input.sourceUnitPrice);
  const economics = explicitTotal != null
    ? sourceEconomicsFromLineTotal(nextSourceQty, explicitTotal)
    : sourceEconomicsFromUnitPrice(nextSourceQty, unitPrice);
  const source = input.conversionSource ?? 'identity';
  const sameUom =
    normalizeUom(input.sourceUom) === normalizeUom(input.stockUom);
  const factor = finitePositive(input.conversionFactor);

  let nextStockQty: number;
  let nextFactor: number | null;
  if (source === 'document_weight') {
    nextStockQty = currentStockQty;
    nextFactor = currentStockQty / nextSourceQty;
  } else if (source === 'metric') {
    const converted = convertQty(
      nextSourceQty,
      input.sourceUom ?? '',
      input.stockUom ?? '',
    );
    nextStockQty = converted ?? (factor != null
      ? nextSourceQty * factor
      : currentStockQty);
    nextFactor = nextStockQty / nextSourceQty;
  } else if (factor != null) {
    nextStockQty = nextSourceQty * factor;
    nextFactor = factor;
  } else if (sameUom) {
    nextStockQty = nextSourceQty;
    nextFactor = 1;
  } else {
    nextStockQty = currentStockQty;
    nextFactor = currentStockQty / nextSourceQty;
  }

  return {
    sourceQty: nextSourceQty,
    stockQty: compactQuantity(nextStockQty),
    sourceUnitPrice: economics.sourceUnitPrice,
    sourceLineTotal: economics.sourceLineTotal,
    conversionFactor: nextFactor,
    conversionSource: source,
    conversionConfirmed:
      source === 'document_weight'
      || source === 'metric'
      || factor != null
      || sameUom,
  };
}

/**
 * Corregge l'UdM letta sul documento senza conservare una trasformazione che
 * apparteneva alla vecchia UdM. Se sorgente e magazzino coincidono la quantità
 * documentale diventa autorevole; tra unità metriche compatibili la conversione
 * è deterministica. Peso/volume e altri passaggi non dimostrabili restano
 * esplicitamente da confermare.
 */
export function changeSourceUomDraft(input: {
  sourceQty: number | null | undefined;
  nextSourceUom: string | null | undefined;
  stockQty: number | string | null | undefined;
  stockUom: string | null | undefined;
}): SourceUomDraftChange {
  const sourceQty = finitePositive(input.sourceQty);
  const stockQty = finitePositive(input.stockQty);
  const sourceUom = input.nextSourceUom?.trim() ?? '';
  const stockUom = input.stockUom?.trim() ?? '';
  const sameUom = sourceUom !== ''
    && normalizeUom(sourceUom) === normalizeUom(stockUom);

  if (sourceQty != null && sameUom) {
    return {
      stockQty: compactQuantity(sourceQty),
      conversionFactor: 1,
      conversionSource: 'identity',
      conversionConfirmed: true,
    };
  }

  const converted = sourceQty != null && sourceUom && stockUom
    ? convertQty(sourceQty, sourceUom, stockUom)
    : null;
  if (sourceQty != null && converted != null && converted > 0) {
    return {
      stockQty: compactQuantity(converted),
      conversionFactor: converted / sourceQty,
      conversionSource: 'metric',
      conversionConfirmed: true,
    };
  }

  return {
    stockQty: stockQty == null ? '' : compactQuantity(stockQty),
    conversionFactor: sourceQty != null && stockQty != null
      ? stockQty / sourceQty
      : null,
    conversionSource: 'user_confirmed',
    conversionConfirmed: false,
  };
}

/**
 * Applica il contenuto unitario al numero di pezzi/colli DOCUMENTALE.
 * Non deve mai usare la quantità stock corrente, che può essere già un peso
 * totale proposto dall'IA (caso T-482: 2 pz, 0,8 kg proposti, 0,175 kg/pz).
 */
export function applyPackFactorDraft(input: {
  sourceQty: number | string | null | undefined;
  factor: number | string | null | undefined;
}): PackFactorDraftApply | null {
  const sourceQty = finitePositive(input.sourceQty);
  const factor = finitePositive(input.factor);
  if (sourceQty == null || factor == null) return null;
  return {
    sourceQty,
    stockQty: compactQuantity(sourceQty * factor),
    factor,
  };
}

/**
 * Modifica della quantità in review.
 *
 * Se documento e stock sono ancora in identità 1:1, l'operatore sta
 * correggendo la quantità letta dall'OCR: aggiorniamo anche source_qty e
 * riconciliamo prezzo/totale. Dopo una conversione (es. STK -> kg), invece,
 * l'edit riguarda solo il movimento stock e l'evidenza documentale resta
 * immutata.
 */
export function editStockQuantityDraft(input: {
  nextQty: number | string | null | undefined;
  stockUom: string | null | undefined;
  sourceQty: number;
  sourceUom: string | null | undefined;
  sourceUnitPrice: number | null;
  sourceLineTotal: number | null;
  conversionFactor: number | null;
  conversionSource: string | null;
}): StockQuantityDraftEdit {
  const nextQty = finitePositive(input.nextQty);
  const sameUom =
    normalizeUom(input.sourceUom) === normalizeUom(input.stockUom);
  const identityFactor = input.conversionFactor == null
    || Math.abs(input.conversionFactor - 1) < 1e-12;
  const identity = sameUom
    && identityFactor
    && (input.conversionSource == null || input.conversionSource === 'identity');
  if (nextQty == null) {
    return {
      qty: typeof input.nextQty === 'string' ? input.nextQty : '',
      sourceQty: input.sourceQty,
      sourceUnitPrice: input.sourceUnitPrice,
      sourceLineTotal: input.sourceLineTotal,
      conversionFactor: input.conversionFactor,
      conversionSource: input.conversionSource ?? (identity ? 'identity' : 'user_confirmed'),
      conversionConfirmed: false,
    };
  }

  if (identity) {
    const explicitTotal = finiteNonNegative(input.sourceLineTotal);
    const unitPrice = finiteNonNegative(input.sourceUnitPrice);
    const economics = explicitTotal != null
      ? sourceEconomicsFromLineTotal(nextQty, explicitTotal)
      : sourceEconomicsFromUnitPrice(nextQty, unitPrice);
    return {
      qty: compactQuantity(nextQty),
      sourceQty: nextQty,
      sourceUnitPrice: economics.sourceUnitPrice,
      sourceLineTotal: economics.sourceLineTotal,
      conversionFactor: 1,
      conversionSource: 'identity',
      conversionConfirmed: true,
    };
  }

  return {
    qty: compactQuantity(nextQty),
    sourceQty: input.sourceQty,
    sourceUnitPrice: input.sourceUnitPrice,
    sourceLineTotal: input.sourceLineTotal,
    conversionFactor: input.sourceQty > 0 ? nextQty / input.sourceQty : null,
    conversionSource: 'user_confirmed',
    conversionConfirmed: true,
  };
}

/**
 * Cambia la coordinata STOCK di una riga review.
 *
 * Le conversioni metriche sono deterministiche e trasformano la quantità
 * corrente (3 kg -> 3000 g). Qualunque cambio non metrico svuota la quantità:
 * l'utente deve inserire il contenuto reale o applicare un fattore collo, così
 * il commit non può interpretare silenziosamente "3 kg" come "3 pz/conf".
 * I campi source_* non fanno parte dell'output e restano quindi immutati.
 */
export function changeStockUomDraft(input: {
  currentQty: number | string | null | undefined;
  currentUom: string | null | undefined;
  nextUom: string | null | undefined;
  sourceQty: number | null | undefined;
  sourceUom: string | null | undefined;
  previousConversionSource?: string | null;
}): StockUomDraftChange {
  const currentQty = finitePositive(input.currentQty);
  const currentUom = input.currentUom?.trim() ?? '';
  const nextUom = input.nextUom?.trim() ?? '';
  const sourceQty = finitePositive(input.sourceQty);
  const sourceUom = input.sourceUom?.trim() ?? '';
  const sameUom = normalizeUom(currentUom) === normalizeUom(nextUom);
  const previousNonMetric = input.previousConversionSource
    && !['identity', 'metric'].includes(input.previousConversionSource)
    ? input.previousConversionSource
    : null;

  if (currentQty != null && nextUom && sameUom) {
    const factor = sourceQty == null ? null : currentQty / sourceQty;
    const sourceSame = normalizeUom(sourceUom) === normalizeUom(nextUom);
    return {
      qty: compactQuantity(currentQty),
      uom: nextUom,
      conversionFactor: factor,
      conversionSource: sourceSame && (factor == null || Math.abs(factor - 1) < 1e-12)
        ? 'identity'
        : (previousNonMetric ?? 'user_confirmed'),
      conversionConfirmed:
        (sourceSame && (factor == null || Math.abs(factor - 1) < 1e-12))
        || previousNonMetric != null,
    };
  }

  const converted = currentQty != null && currentUom && nextUom
    ? convertQty(currentQty, currentUom, nextUom)
    : null;
  if (converted != null && Number.isFinite(converted) && converted > 0) {
    const factor = sourceQty == null ? null : converted / sourceQty;
    const directFromSource = sourceQty != null && sourceUom
      ? convertQty(sourceQty, sourceUom, nextUom)
      : null;
    const directMetric = (
      directFromSource != null
      && normalizeUom(sourceUom) !== normalizeUom(nextUom)
      && Math.abs(directFromSource - converted)
        <= Math.max(1, Math.abs(converted)) * 1e-12
    );
    return {
      qty: compactQuantity(converted),
      uom: nextUom,
      conversionFactor: factor,
      conversionSource: directMetric
        ? 'metric'
        : (previousNonMetric ?? 'user_confirmed'),
      conversionConfirmed: directMetric || previousNonMetric != null,
    };
  }

  return {
    qty: '',
    uom: nextUom,
    conversionFactor: null,
    conversionSource: 'user_confirmed',
    conversionConfirmed: false,
  };
}

export function reconstructImportLines(lines: LoadedLine[], foods: Map<string, FoodLite>): PlanLineDraft[] {
  return lines.map((l) => {
    const food = l.food_id ? foods.get(l.food_id) ?? null : null;
    const name = food?.name ?? l.description ?? '';
    const sourceQty = l.source_qty ?? l.qty;
    const sourceUom = l.source_uom ?? l.uom;
    const sourceUnitPrice = l.source_unit_price ?? l.unit_price;
    const sourceLineTotal = l.source_line_total ?? l.line_total ?? null;
    const stockQty = l.stock_qty ?? l.qty;
    const stockUom = l.stock_uom ?? l.uom;
    const isReusablePackFactor =
      l.conversion_source === 'supplier_alias'
      || l.conversion_source === 'user_confirmed';
    const validation = validateImportLine({
      matchedFoodId: l.food_id,
      matchConfidence: l.food_id ? 1 : null,
      name,
      category: food?.category ?? null,
      uom: stockUom ?? null,
      batchLot: l.batch_lot ?? null,
      qty: typeof stockQty === 'number' ? stockQty : null,
      unitPrice: sourceUnitPrice,
      aiLineConfidence: null,
    });
    return {
      parsed_index: l.seq,
      raw_description: l.description ?? null,
      supplier_code: null,
      food_id: l.food_id,
      food_create: { ...emptyFoodCreate(), name, default_uom: stockUom, category: food?.category ?? '', storage_type: food?.storage_type ?? '' },
      qty: String(stockQty ?? ''),
      uom: stockUom ?? food?.default_uom ?? 'pz',
      batch_lot: l.batch_lot ?? '',
      expiration_date: l.expiration_date ?? '',
      is_frozen: l.is_frozen ?? food?.storage_type === 'surgelato',
      notify_expiration: l.notify_expiration ?? true,
      storage_unit_id: l.storage_unit_id ?? '',
      supplier_sku: l.supplier_sku ?? '',
      skip: false,
      replace_active_lot: false,
      keep_current_lot: l.keep_current_lot ?? false,
      source_qty: sourceQty,
      source_uom: sourceUom,
      source_unit_price: sourceUnitPrice,
      source_line_total: sourceLineTotal,
      currency: (l.currency ?? 'EUR').trim().toUpperCase() || 'EUR',
      // Alias legacy usati ancora dalla UI; restano sempre valori sorgente.
      unit_price: sourceUnitPrice,
      line_total: sourceLineTotal,
      extracted_uom: sourceUom ?? null,
      pack_needs_factor: false,
      pack_factor: isReusablePackFactor ? (l.conversion_factor ?? null) : null,
      pack_base_uom: isReusablePackFactor ? (stockUom ?? null) : null,
      pack_factor_applied: isReusablePackFactor && (l.conversion_factor ?? 1) !== 1,
      conversion_factor: l.conversion_factor ?? (
        sourceQty > 0 && stockQty > 0 ? stockQty / sourceQty : null
      ),
      conversion_source: l.conversion_source ?? null,
      conversion_confirmed: isReusablePackFactor,
      ai_description: null,
      validation,
      gates_ack: false,
      lot_ack: false,
    };
  });
}

/** A product has one stock unit, even when the corrected document has several lots. */
export function patchImportLineDraft(
  lines: PlanLineDraft[], idx: number, patch: Partial<PlanLineDraft>, correcting: boolean,
): PlanLineDraft[] {
  const selected = { ...lines[idx], ...patch };
  return lines.map((line, i) => {
    if (i === idx) return selected;
    if (!correcting || !patch.uom || !selected.food_id
      || line.food_id !== selected.food_id || line.uom === patch.uom) return line;
    const change = changeStockUomDraft({
      currentQty: line.qty, currentUom: line.uom, nextUom: patch.uom,
      sourceQty: line.source_qty, sourceUom: line.source_uom,
      previousConversionSource: line.conversion_source,
    });
    return {
      ...line, uom: change.uom, qty: change.qty,
      conversion_factor: change.conversionFactor,
      conversion_source: change.conversionSource,
      conversion_confirmed: change.conversionConfirmed,
      pack_factor: null, pack_base_uom: null, pack_factor_applied: false,
      pack_needs_factor: !change.conversionConfirmed,
    };
  });
}
